import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Drawer,
  FormControl,
  MenuItem,
  Select,
  Stack,
  IconButton,
  Tooltip,
  FormHelperText,
  Divider,
  Chip,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Edit, Delete as DeleteIcon, VisibilityOutlined } from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import Loader from '../../../Components/Loader';
import { align_center, align_row, dataGridStyle, VM_Colors } from '../../../Styles/VMStyles';
import MUITypography from '../../../Components/MUITypography';
import {
  API_ADD_UPDATE_EVENT_VENDOR,
  API_GET_EVENT_VENDORS_LIST,
  API_GET_REMOVE_EVENT_VENDOR,
} from '../../../Util/URLConstants';
import { checkIsPermissionExist, isEmpty, renderSNo, vendorTypes } from '../../../Util/UtilHelper';
import { canMutateRecord, RECORD_OWNERSHIP_DENIED_MESSAGE } from '../../../Util/recordAuthorization';
import StorageFactory from '../../../Util/StorageFactory';
import { ACCESS_RESTRICTED_MESSAGE, SESSION_USER_COUNTRY_DATA_KEY, SESSION_USER_ID_KEY } from '../../../Util/VMConstants';
import StyledInputField from '../../../Components/Custom/StyledInputField';
import MuiTypographyMandatory from '../../../Components/Custom/MuiTypographyMandatory';
import useCountryList from '../../../Hooks/useCountryList';
import { useSelector } from 'react-redux';
import NoPermission from '../../../Components/NoPermission';

function formatVendorPhone(row) {
  if (row == null) return '—';
  const phone = row.phone_no;
  if (phone == null || String(phone).trim() === '') return '—';
  const cc = row.country_code;
  const prefix = cc != null && String(cc).trim() !== '' ? `+${cc} ` : '';
  return `${prefix}${phone}`;
}

function EnquiryVendors({ customer, readOnly = false }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [viewDrawerOpen, setViewDrawerOpen] = useState(false);
  const [viewVendor, setViewVendor] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [customType, setCustomType] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const { country } = useCountryList();
  const sessionFactory = new StorageFactory('session');
  const userID = sessionFactory.get(SESSION_USER_ID_KEY);
  const cCode = sessionFactory.get(SESSION_USER_COUNTRY_DATA_KEY)?.country_code ;


  const {  permissions } = useSelector(
    (state) => state.subscription
  );

  const [formData, setFormData] = useState({
    vendorType: '',
    vendor_name: '',
    vendor_company: '',
    countryID: '',
    country_code: cCode || '91',
    phone_no: '',
    email: '',
    address: '',
  });

  // Ensure default India selected
  useEffect(() => {
    if (country.length > 0 && !formData.countryID) {
      const india = country.find((c) => c.country_code === '91');
      if (india) {
        setFormData((prev) => ({
          ...prev,
          countryID: india.cid,
          country_code: india.country_code,
        }));
      }
    }
  }, [country, formData.countryID]);

  const handleOpen = () => {
    setViewDrawerOpen(false);
    setViewVendor(null);
    setEditMode(false);
    setDrawerOpen(true);
  };

  const handleClose = () => {
    setDrawerOpen(false);
    setCustomType('');
    setEditMode(false);
    setErrors({});
    setFormData({
      vendorType: '',
      vendor_name: '',
      vendor_company: '',
      countryID: '',
      country_code: '91',
      phone_no: '',
      email: '',
      address: '',
    });
  };




  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    let newErrors = {};

    if (isEmpty(formData.vendorType)) newErrors.vendorType = 'Vendor type is required';
    if (formData.vendorType === 'Others' && isEmpty(customType)) newErrors.customType = 'Custom type is required';
    if (isEmpty(formData.vendor_name)) newErrors.vendor_name = 'Vendor name is required';
    if (isEmpty(formData.vendor_company)) newErrors.vendor_company = 'Vendor company is required';
    if (isEmpty(formData.phone_no)) newErrors.phone_no = 'Phone number is required';
    // else if (!/^\d{15}$/.test(formData.phone_no)) newErrors.phone_no = 'Enter valid 10 digit phone number';
    if (!isEmpty(formData.email) && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = 'Enter valid email address';
    if (isEmpty(formData.address)) newErrors.address = 'Address is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      toast.error('Please enter all mandatory fields');
      return;
    }

    const vendor_type = formData.vendorType === 'Others' ? customType : formData.vendorType;

    const requestBody = {
      user_id: userID,
      enquiry_id: customer.appointment_id,
      vendor_type,
      vendor_name: formData.vendor_name,
      vendor_company: formData.vendor_company,
      country_code: formData.country_code,
      phone_no: formData.phone_no,
      email: formData.email,
      address: formData.address,
    };

    if (editMode) requestBody.id = formData.id;

    try {
      setLoading(true);
      await axios.post(API_ADD_UPDATE_EVENT_VENDOR, requestBody);
      fetchVendors();
      handleClose();
    } catch (error) {
      console.error('Error saving/updating vendor:', error);
      toast.error('Failed to save/update vendor');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenView = (row) => {
    setViewVendor(row);
    setViewDrawerOpen(true);
  };

  const handleCloseView = () => {
    setViewDrawerOpen(false);
    setViewVendor(null);
  };

  const handleEdit = (row) => {
    if (!canMutateRecord(permissions, 'VM036', row)) {
      toast.error(RECORD_OWNERSHIP_DENIED_MESSAGE);
      return;
    }
    setViewDrawerOpen(false);
    setViewVendor(null);
    setCustomType(vendorTypes.includes(row.vendor_type) ? '' : row.vendor_type);
    setFormData({
      vendorType: vendorTypes.includes(row.vendor_type) ? row.vendor_type : 'Others',
      vendor_name: row.vendor_name,
      vendor_company: row.vendor_company,
      countryID: country.find((c) => c.country_code === row.country_code)?.cid || '',
      country_code: row.country_code,
      phone_no: row.phone_no,
      email: row.email,
      address: row.address,
      id: row.id,
    });
    setErrors({});
    setEditMode(true);
    setDrawerOpen(true);
  };

  const handleDelete = (id) => {
    const row = vendors.find((v) => String(v.id) === String(id));
    if (row && !canMutateRecord(permissions, 'VM037', row)) {
      toast.error(RECORD_OWNERSHIP_DENIED_MESSAGE);
      return;
    }
    setLoading(true);
    axios
      .get(`${API_GET_REMOVE_EVENT_VENDOR}?id=${id}`)
      .then(() => fetchVendors())
      .catch((err) => {
        console.error('Error deleting vendor:', err);
        toast.error(err.message);
      })
      .finally(() => setLoading(false));
  };

  const fetchVendors = useCallback(() => {
    setLoading(true);
    axios
      .get(`${API_GET_EVENT_VENDORS_LIST}?user_id=${userID}&enquiry_id=${customer.appointment_id}`)
      .then((resp) => {
        if (resp.data.status_code === 200) {
          // Normalize rows: ensure s_no and reliable id so renderSNo() works
          const list = (resp.data.data || []).map((item, idx) => ({
            ...item,
            s_no: idx + 1,
            id: item.id || item.vendor_id || `vendor-${idx + 1}`,
          }));
          setVendors(list);
        } else toast.error(resp.data.message);
      })
      .catch((err) => {
        console.error('Error fetching vendors:', err);
        toast.error(err.message);
      })
      .finally(() => setLoading(false));
  }, [customer.appointment_id, userID]);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  const handleCountryChange = (e) => {
    const selectedCid = e.target.value;
    const selectedObj = country.find((c) => c.cid === selectedCid);
    handleChange('countryID', selectedCid);
    setFormData((prev) => ({
      ...prev,
      country_code: selectedObj?.country_code || '',
    }));
  };

  const columns = [  
    renderSNo(),
    { field: 'vendor_type', headerName: 'Type', flex: 1, minWidth: 100 },
    { field: 'vendor_name', headerName: 'Name', flex: 1, 
      // minWidth: 150 
    },
    { field: 'vendor_company', headerName: 'Company', flex: 1, minWidth: 120 },
    // { field: 'phone_no', headerName: 'Phone no', flex: 1, minWidth: 150 },
    // {
    //   field: 'created_by',
    //   headerName: 'Created By',
    //   flex: 1.2,
    //   minWidth: 150,
    //   valueGetter: (params) => params?.name || '-',
    // },
    {
      field: 'view',
      headerName: 'View',
      flex: 0.55,
      minWidth: 108,
      sortable: false,
      disableColumnMenu: true,
      cellClassName: 'enquiry-vendors-view-cell',
      renderCell: (params) => (
        <Button
          variant="outlined"
          size="small"
          startIcon={<VisibilityOutlined sx={{ fontSize: 18 }} />}
          onClick={() => handleOpenView(params.row)}
          sx={{
            textTransform: 'none',
            fontFamily: 'Lato',
            fontWeight: 600,
            fontSize: '0.8125rem',
            borderRadius: 1,
            borderColor: '#1E3A8A',
            color: '#1E3A8A',
            px: 1.25,
            py: 0.5,
            minWidth: 0,
            '&:hover': {
              borderColor: '#1E40AF',
              backgroundColor: 'rgba(30, 58, 138, 0.06)',
            },
          }}
        >
          View
        </Button>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.6,
      minWidth: 100,
      cellClassName: 'enquiry-vendors-actions-cell',
      renderCell: (params) => (
        <Box display="flex" gap={1} sx={{ alignItems: 'center' }}>
          {
          !readOnly && canMutateRecord(permissions, "VM036", params.row) &&
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => handleEdit(params.row)}>
              <Edit fontSize="small" color="primary" />
            </IconButton>
          </Tooltip>
        }
        {
          !readOnly && canMutateRecord(permissions, "VM037", params.row) &&
          <Tooltip title="Delete">
            <IconButton size="small" onClick={() => handleDelete(params.row.id)}>
              <DeleteIcon fontSize="small" color="error" />
            </IconButton>
          </Tooltip>
        }
           </Box>
      ),
    },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column', // Stack content vertically
        padding: { xs: '16px', sm: '24px' }, // Adjust padding for smaller screens
        gap: '16px',
        width: '100%',
        maxWidth: '100%', // Ensure it takes full width on smaller screens
        backgroundColor: '#FFFFFF',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' }, // Stack elements vertically on smaller screens
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          mb: 2,
          gap: { xs: 2, sm: 0 }, // Add gap between elements on smaller screens
        }}
      >
        <MUITypography
          variant="h4"
          gutterBottom
          sx={{
            fontWeight: 700,
            color: '#1E1E1E',
            flexGrow: 1,
          }}
        >
          Event Vendors
        </MUITypography>
        {!readOnly && checkIsPermissionExist(permissions, 'VM050') && (
          <Button
            variant="contained"
            color="primary"
            sx={{
              width: { xs: '100%', sm: '175px' },
              height: '44px',
              borderRadius: '4px',
              fontFamily: 'Lato',
              fontSize: '16px',
              fontWeight: 400,
              lineHeight: '24px',
            }}
            onClick={handleOpen}
          >
            Add Event Vendor
          </Button>
        )}
      </Box>
  
      {/* Vendor Table */}
      {vendors.length === 0 ? (
        <Box
          sx={{
            ...align_center,
            ...align_row,
            width: '100%',
            height: 260,
            flexDirection: 'column',
            backgroundColor: VM_Colors.white2,
          }}
        >
          <Box
            component="img"
            src={'/static/vendor.svg'}
            alt="Vendor"
            sx={{ mt: 5, height: 50 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            No vendors added yet!
          </Typography>
        </Box>
      ) : checkIsPermissionExist(permissions, 'VM035') ? (
        <Box
          sx={{
            width: '100%',
            bgcolor: '#F3F3F3',
            borderRadius: 2,
            overflowX: 'auto', // Enable horizontal scrolling
          }}
        >
          <Box sx={{ minWidth: 600 }}>
            <DataGrid
              rows={vendors}
              columns={columns.map((col) => ({
                ...col,
                sortable: false,
                filterable: false,
                hideable: false,
              }))}
              getRowId={(row) => row.id}
              pageSize={5}
              rowsPerPageOptions={[5]}
              getRowHeight={() => 'auto'}
              autoHeight={false}
              sx={{
                ...dataGridStyle,
                minWidth: 560,
                '& .MuiDataGrid-cell.enquiry-vendors-view-cell, & .MuiDataGrid-cell.enquiry-vendors-actions-cell':
                  {
                    alignItems: 'center',
                  },
              }}
            />
          </Box>
        </Box>
      ) : (
        <Box sx={{ mt: 4 }}>
          <NoPermission message={ACCESS_RESTRICTED_MESSAGE} />
        </Box>
      )}
  
      {/* Drawer Form */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: '400px' }, // Full width on smaller screens
            p: 3,
          },
        }}
      >
        <Typography
          variant="h3"
          sx={{
            ...align_center,
            fontFamily: 'Lato',
            mb: 2,
          }}
        >
          {editMode ? 'Update Vendor' : 'Add Vendor'}
        </Typography>
  
        <Stack spacing={2}>
          <MuiTypographyMandatory label="Vendor Type" required />
          <FormControl fullWidth error={!!errors.vendorType}>
            <Select
              value={formData.vendorType}
              onChange={(e) => handleChange('vendorType', e.target.value)}
              displayEmpty
            >
              <MenuItem value="" disabled>
                -- Select --
              </MenuItem>
              {vendorTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
            {errors.vendorType && <FormHelperText>{errors.vendorType}</FormHelperText>}
          </FormControl>
  
          {formData.vendorType === 'Others' && (
            <StyledInputField
              fullWidth
              heading="Custom Type"
              value={customType}
              onChange={(e) => setCustomType(e.target.value)}
              error={!!errors.customType}
              helperText={errors.customType}
            />
          )}
  
          <StyledInputField
            fullWidth
            heading="Vendor Name"
            value={formData.vendor_name}
            onChange={(e) => handleChange('vendor_name', e.target.value)}
            error={!!errors.vendor_name}
            helperText={errors.vendor_name}
            required
          />
          <StyledInputField
            fullWidth
            heading="Vendor Company"
            value={formData.vendor_company}
            onChange={(e) => handleChange('vendor_company', e.target.value)}
            error={!!errors.vendor_company}
            helperText={errors.vendor_company}
            required
          />
  
          {/* Country Code + Phone */}
          <Box sx={{ ...align_row, width: '100%', gap: 2, mt: 2 }}>
            <Box sx={{ minWidth: '120px' }}>
              <MuiTypographyMandatory label="Country" required />
              <FormControl fullWidth>
                <Select
                  name="countryID"
                  value={formData.countryID}
                  onChange={handleCountryChange}
                >
                  {country.map((ctr) => (
                    <MenuItem key={ctr.cid} value={ctr.cid}>
                      {ctr.country_name} ({ctr.country_code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
  
            <Box sx={{ flexGrow: 1 }}>
              <StyledInputField
                heading="Mobile No"
                required
                fullWidth
                type="number"
                value={formData.phone_no}
                onChange={(e) => {
                  const digitsOnly = (e.target.value || '').replace(/\D/g, '');
                  handleChange('phone_no', digitsOnly);
                }}
                inputProps={{ maxLength: 15, inputMode: 'numeric', pattern: '[0-9]*' }}
                error={!!errors.phone_no}
                helperText={errors.phone_no}
              />
            </Box>
          </Box>
  
          <StyledInputField
            fullWidth
            heading="Email (Optional)"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            error={!!errors.email}
            helperText={errors.email}
          />
          <StyledInputField
            fullWidth
            multiline
            heading="Address"
            value={formData.address}
            onChange={(e) => handleChange('address', e.target.value)}
            error={!!errors.address}
            helperText={errors.address}
            required
          />
  
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, pb: 10 }}>
            <Button onClick={handleClose} sx={{ mr: 2 }}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSubmit}>
              {editMode ? 'Update' : 'Submit'}
            </Button>
          </Box>
        </Stack>
      </Drawer>

      {/* View vendor — gradient header + cards (original layout) */}
      <Drawer
        anchor="right"
        open={viewDrawerOpen}
        onClose={handleCloseView}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 440, md: 480 },
            maxWidth: '100%',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#F8FAFC',
          },
        }}
      >
        {viewVendor && (
          <>
            <Box
              sx={{
                background: 'linear-gradient(135deg, #1E3A8A 0%, #1E40AF 55%, #2563EB 100%)',
                color: '#fff',
                px: 3,
                pt: 3,
                pb: 2.5,
              }}
            >
              <Typography
                variant="overline"
                sx={{ letterSpacing: '0.12em', opacity: 0.85, display: 'block', mb: 0.5 }}
              >
                Vendor details
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: 'Lato', lineHeight: 1.25 }}>
                {viewVendor.vendor_name || '—'}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1.5 }}>
                {viewVendor.vendor_type && (
                  <Chip
                    label={viewVendor.vendor_type}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      color: '#fff',
                      fontWeight: 600,
                      border: '1px solid rgba(255,255,255,0.35)',
                    }}
                  />
                )}
                {viewVendor.vendor_company && (
                  <Chip
                    label={viewVendor.vendor_company}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: 'rgba(255,255,255,0.55)',
                      color: '#fff',
                      fontWeight: 500,
                      maxWidth: '100%',
                      '& .MuiChip-label': { whiteSpace: 'normal', textAlign: 'left' },
                    }}
                  />
                )}
              </Stack>
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2.5 }}>
              <Stack spacing={2.5}>
                <Box
                  sx={{
                    bgcolor: '#fff',
                    borderRadius: 2,
                    border: '1px solid #E2E8F0',
                    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
                    p: 2,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: '#64748B',
                      fontWeight: 700,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      fontSize: '0.7rem',
                      mb: 1.5,
                    }}
                  >
                    Contact
                  </Typography>
                  <Stack spacing={1.75}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
                        Phone
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#0F172A' }}>
                        {formatVendorPhone(viewVendor)}
                      </Typography>
                    </Box>
                    <Divider flexItem sx={{ borderColor: '#F1F5F9' }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
                        Email
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500, color: '#0F172A', wordBreak: 'break-all' }}
                      >
                        {viewVendor.email && String(viewVendor.email).trim() !== ''
                          ? viewVendor.email
                          : '—'}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                <Box
                  sx={{
                    bgcolor: '#fff',
                    borderRadius: 2,
                    border: '1px solid #E2E8F0',
                    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
                    p: 2,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: '#64748B',
                      fontWeight: 700,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      fontSize: '0.7rem',
                      mb: 1.25,
                    }}
                  >
                    Address
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#334155',
                      lineHeight: 1.65,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {viewVendor.address && String(viewVendor.address).trim() !== ''
                      ? viewVendor.address
                      : '—'}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    bgcolor: '#fff',
                    borderRadius: 2,
                    border: '1px solid #E2E8F0',
                    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
                    p: 2,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: '#64748B',
                      fontWeight: 700,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      fontSize: '0.7rem',
                      mb: 1.25,
                    }}
                  >
                    Record
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.35 }}>
                    Created by
                  </Typography>
                  {(() => {
                    const cb = viewVendor.created_by;
                    if (cb == null || cb === '') {
                      return (
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#0F172A' }}>
                          —
                        </Typography>
                      );
                    }
                    if (typeof cb === 'string') {
                      return (
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#0F172A' }}>
                          {cb}
                        </Typography>
                      );
                    }
                    const cc =
                      cb.country_code != null && String(cb.country_code).trim() !== ''
                        ? `+${cb.country_code} `
                        : '';
                    const ph =
                      cb.mobile_no != null && String(cb.mobile_no).trim() !== ''
                        ? `${cc}${cb.mobile_no}`
                        : null;
                    const headline = cb.name || cb.email || ph || '—';
                    const extras = [];
                    if (cb.name && cb.email) extras.push({ key: 'email', label: 'Email', value: cb.email });
                    if (cb.name && ph) extras.push({ key: 'phone', label: 'Phone', value: ph });
                    if (!cb.name && cb.email && ph) extras.push({ key: 'phone', label: 'Phone', value: ph });
                    return (
                      <Stack spacing={0.75}>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#0F172A' }}>
                          {headline}
                        </Typography>
                        {extras.map((row) => (
                          <Typography key={row.key} variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                            <Box component="span" sx={{ fontWeight: 600, color: '#64748B', mr: 0.75 }}>
                              {row.label}
                            </Box>
                            {row.value}
                          </Typography>
                        ))}
                      </Stack>
                    );
                  })()}
                </Box>
              </Stack>
            </Box>

            <Box
              sx={{
                px: 3,
                py: 2,
                borderTop: '1px solid #E2E8F0',
                bgcolor: '#fff',
                flexShrink: 0,
              }}
            >
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleCloseView}
                sx={{
                  fontFamily: 'Lato',
                  py: 1.25,
                  borderRadius: 1,
                }}
              >
                Close
              </Button>
            </Box>
          </>
        )}
      </Drawer>
  
      {loading && <Loader loading={loading} />}
    </Box>
  );
}

export default EnquiryVendors;
